import { type FC, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';
import { isCompactTileSize, type TileSize } from '../constants.ts';

import { useShippingPrice } from '../hooks/useShippingPrice.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { type ProductShipping_viewer$key } from './__generated__/ProductShipping_viewer.graphql.ts';
import { type ProductShipping_item$key } from './__generated__/ProductShipping_item.graphql.ts';

const viewerFragment = graphql`
    fragment ProductShipping_viewer on Viewer {
        ...useShippingPriceV2_viewer
    }
`;
const itemFragment = graphql`
    fragment ProductShipping_item on Item {
        ...useShippingPriceV2_item
    }
`;

type ProductShippingProps = {
    viewer: ProductShipping_viewer$key | null | undefined;
    item: ProductShipping_item$key;
    currency: string;
    shippingPrices?: Record<string, number> | null;
    showShippingCost?: boolean;
    isShippingCostSbVariant?: boolean;
};

const FreeShippingText: FC<{ className: string }> = ({ className }) => (
    <span className={classnames(dibsCss.textInternalsecondary, className)} data-tn="free-shipping">
        <FormattedMessage id="dc.buyerProductTile.freeShipping" defaultMessage="Free Shipping" />
    </span>
);

const ProductShipping: FC<ProductShippingProps> = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const amount = useShippingPrice({ item, viewer, currency });
    if (amount === 0) {
        return (
            <p className={dibsCss.mt2px}>
                <FreeShippingText className={dibsCss.textBodyXSmallHeavy} />
            </p>
        );
    }
    return null;
};

type ProductShippingInfoProps = {
    shippingPrices: Record<string, number> | null | undefined;
    currency: string;
    size: TileSize;
};

const ProductShippingInfo: FC<ProductShippingInfoProps> = ({ shippingPrices, currency, size }) => {
    const content = useMemo(() => {
        if (typeof shippingPrices === 'undefined') {
            return undefined;
        }

        if (shippingPrices === null) {
            return (
                <FormattedMessage
                    id="dc.buyerProductTile.shippingRatesLoading"
                    defaultMessage="Fetching shipping{ellipsis}"
                    values={{
                        ellipsis: <span className={dibsCss.loadingDots} />,
                    }}
                />
            );
        }

        const amount =
            typeof shippingPrices[currency] === 'number'
                ? Math.floor(shippingPrices[currency])
                : null;
        if (amount === 0) {
            return (
                <FreeShippingText
                    className={
                        isCompactTileSize(size)
                            ? dibsCss.textBodyXSmallHeavy
                            : dibsCss.textBodySmallHeavy
                    }
                />
            );
        } else if (amount === null) {
            return (
                <FormattedMessage
                    id="dc.buyerProductTile.shippingRatesVary"
                    defaultMessage="Shipping rates vary"
                />
            );
        } else {
            return (
                <FormattedMessage
                    id="dc.buyerProductTile.shippingPrice"
                    defaultMessage="{price} shipping"
                    values={{
                        price: <FormattedPrice currency={currency} value={amount} />,
                    }}
                />
            );
        }
    }, [shippingPrices, currency, size]);

    return content ? (
        <p
            className={classnames(
                dibsCss.mt2px,
                isCompactTileSize(size) ? dibsCss.textBodyXSmallNormal : dibsCss.textBodySmallNormal
            )}
        >
            {content}
        </p>
    ) : null;
};

export const ProductShippingWrapper: FC<ProductShippingProps & ProductShippingInfoProps> = ({
    viewer,
    item,
    currency,
    isShippingCostSbVariant,
    shippingPrices,
    size,
}) => {
    return isShippingCostSbVariant ? (
        <ProductShippingInfo shippingPrices={shippingPrices} currency={currency} size={size} />
    ) : (
        <ProductShipping viewer={viewer} item={item} currency={currency} />
    );
};
